import { Icon } from '../../components/Icon.tsx';
import type { Product } from './products.ts';
import { SpecimenPlate } from './SpecimenPlate.tsx';
import { SCREENS } from './screens/index.tsx';

/**
 * One product, told with type and space rather than containers.
 *
 * There is no panel and no tonal band: the copy sits directly on the page, the
 * app screen floats beside it on a soft shadow, and the only saturated things
 * are the category label, the feature dots and the link. Sides alternate so the
 * page has rhythm without needing alternating background fills.
 */
export function ProductSection({
  product,
  index,
}: {
  product: Product;
  index: number;
}): JSX.Element {
  const flip = index % 2 === 1;
  const Screen = SCREENS[product.key];
  const host = product.url.replace(/^https?:\/\//, '');

  return (
    <section id={product.key} className="folio-wide scroll-mt-24 py-20 sm:py-28">
      <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-2 lg:gap-24">
        {/* copy */}
        <div
          className={`reveal min-w-0 ${flip ? 'lg:order-2' : ''}`}
          style={{ '--reveal-delay': '60ms' } as React.CSSProperties}
        >
          <span className="chip-dv">
            <Icon name={product.icon} size={18} />
            {product.category}
          </span>

          <h2 className="head-dv mt-4 text-[2.1rem] leading-[1.3] sm:text-[2.9rem] sm:leading-[1.25]">
            {product.name}
          </h2>

          <p className="head-dv mt-4 max-w-md text-xl leading-relaxed text-muted-foreground">
            {product.tagline}
          </p>

          <p className="mt-5 max-w-md leading-loose text-muted-foreground">{product.blurb}</p>

          <ul className="spec max-w-md">
            {product.bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>

          <div className="mt-9 flex flex-wrap items-center gap-2">
            {product.key === 'wapmeet' ? (
              <>
                <a href={`${product.url}/demo`} target="_blank" rel="noreferrer" className="btn-primary">
                  ލައިވް ޑިމޯ <Icon name="open_in_new" size={18} />
                </a>
                <a href={product.url} target="_blank" rel="noreferrer" className="btn-ghost">
                  ލޮގިން
                </a>
              </>
            ) : (
              <a href={product.url} target="_blank" rel="noreferrer" className="btn-ghost !px-0">
                {product.name} ހުޅުވާ <Icon name="open_in_new" size={18} />
              </a>
            )}
            {product.inDevelopment && (
              <span className="chip-warn ms-1">
                <span className="head-dv leading-none">ޑިވެލޮޕްކުރަމުން ދަނީ</span>
              </span>
            )}
          </div>
        </div>

        {/* the app itself, floating */}
        <div
          className={`reveal min-w-0 ${flip ? 'lg:order-1' : ''}`}
          style={{ '--reveal-delay': '160ms' } as React.CSSProperties}
        >
          <SpecimenPlate host={host}>{Screen ? <Screen /> : null}</SpecimenPlate>
        </div>
      </div>
    </section>
  );
}
